// DoughnutChart.js
import React from "react";
import { Doughnut } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import { useIsDarkTheme } from "helpers/darktheme";

ChartJS.register(ArcElement, Tooltip, Legend);

const DoughnutChart = ({ dataItem }) => {
    const inprogress = dataItem?.["In Progress"] || 0;
    const Done = dataItem?.["Done"] || 0;
    const Backlog = dataItem?.["Backlog"] || 0;
    const inreview = dataItem?.["In Review"] || 0;
    const todo = dataItem?.["To Do"] || 0;
    const isDarkTheme = useIsDarkTheme();
    const [chartKey, setChartKey] = React.useState(0);

    // Re-render the chart when theme changes
    React.useEffect(() => {
        setChartKey((prevKey) => prevKey + 1);
    }, [isDarkTheme]);

    const data = {
        labels: ["In Progress", "Done", "Backlog", "In Review", "To Do"],
        datasets: [
            {
                data: [Backlog, Done, inprogress, inreview, todo],
                backgroundColor: [
                    "#F0F0F0",
                    "#12B76A",
                    "#1F78B4",
                    "#E37915",
                    "#D52728",
                ],
                hoverBorderColor: [
                    "rgb(0, 255, 0)",
                    "rgb(0, 255, 0)",
                    "rgb(0, 255, 0)",
                ],
                borderColor: ["white"],
                hoverBorderWidth: 2,
                hoverOffset: 4,
                borderWidth: 3,
            },
        ],
    };

    const options = {
        plugins: {
            legend: {
                display: false,
            },
            tooltip: {
                callbacks: {
                    label: function (tooltipItem) {
                        const total = data.datasets[0].data.reduce(
                            (acc, value) => acc + value,
                            0
                        );
                        const value =
                            data.datasets[0].data[tooltipItem.dataIndex];
                        const percentage = ((value / total) * 100).toFixed(2);
                        const label = data.labels[tooltipItem.dataIndex];
                        return [`${label}`, `${value} (${percentage}%)`];
                    },
                    title: function () {
                        return "";
                    },
                },
            },
        },
        cutout: "50%",
    };

    // Custom plugin to display the highest dataset value with dynamic color

    React.useEffect(() => {
        const customCenterPlugin = {
            id: "centerValue",
            beforeDraw(chart) {
                const { width } = chart;
                const { ctx } = chart;
                const datasets = chart.data.datasets[0];

                if (
                    !datasets ||
                    !Array.isArray(datasets.data) ||
                    datasets.data.length === 0
                ) {
                    console.warn(
                        "Dataset data is missing or not an array",
                        datasets
                    );
                    return;
                }

                const highestValue = Math.max(...datasets?.data); // Get the highest value
                const highestIndex = datasets.data.indexOf(highestValue); // Get the index of the highest value
                const highestColor = datasets.backgroundColor[highestIndex]; // Get the corresponding color
                const highestLabel = chart.data.labels[highestIndex]; // Get the corresponding label

                // Add the text in the center
                ctx.save();
                ctx.font = "bold 40px Arial"; // Adjust font size and style
                ctx.fillStyle = highestColor; // Set text color to the corresponding dataset color
                ctx.textAlign = "center";
                ctx.textBaseline = "middle";

                // First Line: Value
                ctx.fillText(
                    highestValue,
                    width / 2,
                    chart.chartArea.height / 2 - 20
                );

                // Second Line: Label
                ctx.font = "16px Arial"; // Adjust font for label
                ctx.fillStyle = isDarkTheme ? "#595959" : "#ffffff"; // Label text remains black
                ctx.fillText(
                    highestLabel,
                    width / 2,
                    chart.chartArea.height / 2 + 20
                );
                ctx.restore();
            },
        };

        // Register the plugin
        ChartJS.register(customCenterPlugin);
        return () => {
            ChartJS.unregister(customCenterPlugin); // Unregister on unmount
        };
    }, [isDarkTheme]);

    return (
        <div style={{ width: "318px", height: "318px", margin: "0 auto" }}>
            <Doughnut key={chartKey} data={data} options={options} />
        </div>
    );
};

export default DoughnutChart;
